import FormErrors from 'app/utils/form_errors'
import get from 'lodash/get'
import { default as deserialize, formatMeta } from 'app/utils/deserialize'
import { fetch, create, remove, update } from 'app/utils/data'
import { buildFilters } from './utils'

export default {
  namespaced: true,

  state: {
    list: [],
    item: null,
    pagination: {}
  },

  getters: {
    currentAccounts: (state) => state.list,
    currentAccount: (state) => state.item,
    currentPagination: (state) => state.pagination
  },

  mutations: {
    accountsFetched(state, { accounts }) {
      state.list = accounts
    },

    accountFetched(state, { account }) {
      state.item = account
    },

    paginationFetched(state, { pagination }) {
      state.pagination = pagination
    }
  },

  actions: {
    async fetchAccounts({ commit }, { page = 1, filters = {} }) {
      const response = await fetch({
        url: '/api/admin/accounts',
        params: {
          page,
          ...buildFilters(filters)
        }
      })

      const accounts = await deserialize(response.data)
      const pagination = formatMeta(get(response.data, 'meta', {}))

      commit('accountsFetched', { accounts })
      commit('paginationFetched', { pagination })
    },

    async fetchAccount({ commit }, accountId) {
      const response = await fetch({ url: `/api/admin/accounts/${accountId}` })
      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    },

    async fetchAccountByEmail({ commit }, email) {
      const response = await fetch({ url: `/api/admin/accounts/lookup/${encodeURIComponent(email)}` })

      if (response.data && response.data.data !== null) {
        const account = await deserialize(response.data)
        commit('accountFetched', { account })
        return true
      } else {
        return false
      }
    },

    async deleteAccount(_context, accountId) {
      await remove({ url: `/api/admin/accounts/${accountId}` })
    },

    async deleteInvoice({ dispatch }, { accountId, invoiceId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/invoices/${invoiceId}` })

      dispatch('fetchAccount', accountId)
    },

    async updateExternalToken({ dispatch }, { accountId, active }) {
      const changes = { active: active }

      await update({
        url: `/api/admin/tools/token/${accountId}`,
        patch: true,
        changes
      })

      dispatch('fetchAccount', accountId)
    },

    async passwordReset({ dispatch }, data) {
      const response = await create({
        url: '/api/admin/tools/passwords',
        data: data
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async confirmAccount({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/confirmations',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async resendConfirmation({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/confirmations/resend',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async resendTeamInvitation({ dispatch }, { accountId, teamMemberEmail }) {
      try {
        await create({
          url: '/api/admin/tools/team-invite',
          data: {
            email: teamMemberEmail
          }
        })

        dispatch('fetchAccount', accountId)
      } catch (error) {
        throw new FormErrors(error.response.data.error)
      }
    },

    async transferLicense({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/transfer',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async cancelSubscription({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/subscriptions',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async reconcileSubscription({ dispatch }, changes) {
      const response = await create({
        url: '/api/admin/tools/subscriptions/reconcile',
        data: changes
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async updateAccount({ state, dispatch }, changes) {
      const response = await update({
        url: `/api/admin/accounts/${state.item.id}`,
        patch: true,
        type: 'account',
        changes,
        record: state.item
      })

      const account = await deserialize(response.data)

      dispatch('fetchAccount', account.id)
    },

    async activateServices({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/services` })

      dispatch('fetchAccount', accountId)
    },

    async deactivateServices({ dispatch }, { accountId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/services` })

      dispatch('fetchAccount', accountId)
    },

    async changePaymentMethod({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/payment-method` })

      dispatch('fetchAccount', accountId)
    },

    async createMissingInvoice({ dispatch }, { accountId }) {
      await create({ url: `/api/admin/accounts/${accountId}/invoices`, data: {} })

      dispatch('fetchAccount', accountId)
    },

    async activateKits({ dispatch }, { accountId }) {
      await update({ url: `/api/admin/accounts/${accountId}/kits` })

      dispatch('fetchAccount', accountId)
    },

    async deactivateKits({ dispatch }, { accountId }) {
      await remove({ url: `/api/admin/accounts/${accountId}/kits` })

      dispatch('fetchAccount', accountId)
    },

    async toggleKitsShutdown({ commit, state }, { accountId, toggle }) {
      const changes = { neverShutdown: toggle }

      const response = await update({
        url: `/api/admin/accounts/${accountId}/kits`,
        patch: true,
        type: 'account',
        changes,
        record: state.item
      })

      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    },

    async toggleMeteredBandwidth({ commit, state }, { id, toggle }) {
      const changes = { meteredBandwidthOn: toggle }

      const response = await update({
        url: `/api/admin/metered-billing/${id}`,
        patch: true,
        changes,
        record: state.item
      })

      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    },

    async toggleMeteredPageviews({ commit, state }, { id, toggle }) {
      const changes = { meteredPageviewsOn: toggle }

      const response = await update({
        url: `/api/admin/metered-billing/${id}`,
        patch: true,
        changes,
        record: state.item
      })

      const account = await deserialize(response.data)

      commit('accountFetched', { account })
    }
  }
}
